import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Autocomplete,
  Paper
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { UnitSystem, CustomUnitFields } from 'providers/api';
import { Trans } from '@lingui/react';
import { Weaken } from 'utils';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import UnitSystemNumberField from '../../../StopePanelPage/RecipesTab/RecipeForm/UnitSystemNumberField';
import { NodeRow } from '../../types';
import { Model } from '../../../hooks/useModelState';
import { PCNode } from '../../../MineModel/Nodes';

interface StringNodesTableProps {
  model: Model;
  unitSystem: UnitSystem;
  customUnitFields: CustomUnitFields[];
  stringRows: Weaken<NodeRow>[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string | React.ReactNode;
  hasError: (field: string) => boolean;
  getErrorHelpText: (field: string) => string;
  handleStringRowChange: (field: string, value: any) => void;
  handleCoordinateChange: (field: string, value: string) => void;
  handleDeleteRow: (index: number) => void;
  handleAddRow: () => void;
}

const hasNodeId = (node: any): node is { nodeId: string } => node?.nodeId;
const hasId = (node: any): node is { id: string } => node?.id;

const StringNodesTable: React.FC<StringNodesTableProps> = ({
  model,
  unitSystem,
  customUnitFields,
  stringRows,
  hasError = () => false,
  getErrorHelpText = () => '',
  handleStringRowChange,
  handleCoordinateChange,
  handleDeleteRow: handleDeleteColumn,
}) => {
  const existingNodes = Object.values(model.nodes);
  const pipeTypes = Object.values(model.pipes);

  // Generic helper function for all error texts
  const getFieldErrorText = (index: number, fieldName: string) => {
    const path = `nodes.${index}.${fieldName}`;
    if (hasError(path)) {
      return getErrorHelpText(path);
    }
    return '';
  };

  // Helper function to find a node by ID
  const findNodeById = (id: string | undefined) => {
    if (!id) return null;
    return existingNodes.find((node) => node.id === id) || null;
  };

  // Helper function to find a pipe by ID
  const findPipeById = (id: string | undefined) => {
    if (!id) return null;
    return pipeTypes.find((pipe) => pipe.pipeId === id) || null;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        '& .MuiTable-root': {
          tableLayout: 'fixed',
        },
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><Trans id="Field">Field</Trans></TableCell>
            {stringRows.map((_, index) => (
              <TableCell key={_.nodeId}>
                <Trans id={`node ${index + 1}`}>
                  Node
                  {index + 1}
                </Trans>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><Trans id="Node ID">Node ID</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={1}>
                <TextField
                  size="small"
                  value={row.nodeId || ''}
                  onChange={(e) => handleStringRowChange(`nodes.${index}.nodeId`, e.target.value)}
                  error={hasError(`nodes.${index}.nodeId`)}
                  required
                  helperText={getFieldErrorText(index, 'nodeId')}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell>
              <IconButton
                size="small"
                onClick={() => handleDeleteColumn(stringRows.length - 1)}
                disabled={stringRows.length === 1}
              >
                <Delete />
              </IconButton>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Previous Node">Previous Node</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <Autocomplete
                  size="small"
                  options={existingNodes}
                  getOptionLabel={(option) => {
                    if (hasNodeId(option)) return option.nodeId;
                    if (hasId(option)) return option.id;
                    return '';
                  }}
                  value={index === 0 ? findNodeById(row.previousNodeId?.[0]) : findNodeById(stringRows[index - 1].nodeId)}
                  onChange={(_, value) => handleStringRowChange(`nodes.${index}.previousNodeId`, value ? [value.id] : [])}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={hasError(`nodes.${index}.previousNodeId`)}
                      helperText={getFieldErrorText(index, 'previousNodeId')}
                    />
                  )}
                  disabled={index !== 0}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Pipe Type">Pipe Type</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <Autocomplete
                  size="small"
                  options={pipeTypes}
                  getOptionLabel={(option) => option.name || ''}
                  value={findPipeById(row.pipeType)}
                  onChange={(_, value) => handleStringRowChange(`nodes.${index}.pipeType`, value ? value.pipeId : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={hasError(`nodes.${index}.pipeType`)}
                      helperText={getFieldErrorText(index, 'pipeType')}
                    />
                  )}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="X">X</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <UnitSystemNumberField
                  unitSystem={unitSystem}
                  customUnitFields={customUnitFields}
                  unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                  value={row.x || ''}
                  onChange={(e) => handleCoordinateChange(`nodes.${index}.x`, e.target.value)}
                  decimalPlaces={0}
                  size="small"
                  id="x"
                  name="x"
                  error={hasError(`nodes.${index}.x`)}
                  helperText={getFieldErrorText(index, 'x')}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Y">Y</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <UnitSystemNumberField
                  unitSystem={unitSystem}
                  customUnitFields={customUnitFields}
                  unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                  value={row.y || ''}
                  onChange={(e) => handleCoordinateChange(`nodes.${index}.y`, e.target.value)}
                  decimalPlaces={0}
                  size="small"
                  id="y"
                  name="y"
                  error={hasError(`nodes.${index}.y`)}
                  helperText={getFieldErrorText(index, 'y')}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Z">Z</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <UnitSystemNumberField
                  unitSystem={unitSystem}
                  customUnitFields={customUnitFields}
                  unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                  value={row.z || ''}
                  onChange={(e) => handleCoordinateChange(`nodes.${index}.z`, e.target.value)}
                  decimalPlaces={0}
                  size="small"
                  id="z"
                  name="z"
                  error={hasError(`nodes.${index}.z`)}
                  helperText={getFieldErrorText(index, 'z')}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Pit">Pit</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <TextField
                  fullWidth
                  size="small"
                  value={row.pitName || ''}
                  onChange={(e) => handleStringRowChange(`nodes.${index}.pitName`, e.target.value)}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Area">Area</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <TextField
                  fullWidth
                  size="small"
                  value={row.area || ''}
                  onChange={(e) => handleStringRowChange(`nodes.${index}.area`, e.target.value)}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Level">Level</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <TextField
                  fullWidth
                  size="small"
                  value={row.level || ''}
                  onChange={(e) => handleStringRowChange(`nodes.${index}.level`, e.target.value)}
                  error={hasError(`nodes.${index}.level`)}
                  helperText={getFieldErrorText(index, 'level')}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="Friction Loop Length">Friction Loop Length</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <UnitSystemNumberField
                  unitSystem={unitSystem}
                  customUnitFields={customUnitFields}
                  unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                  value={row.frictionLoopLength || 0}
                  onChange={(e) => handleCoordinateChange(`nodes.${index}.frictionLoopLength`, e.target.value)}
                  decimalPlaces={0}
                  size="small"
                  fullWidth
                  error={hasError(`nodes.${index}.frictionLoopLength`)}
                  helperText={getFieldErrorText(index, 'frictionLoopLength')}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell><Trans id="End At">End At</Trans></TableCell>
            {stringRows.map((row, index) => (
              <TableCell key={row.nodeId}>
                <Autocomplete
                  size="small"
                  options={existingNodes.map((node: PCNode) => node.id)}
                  value={row.endAtExistingNode || ''}
                  onChange={(_, value) => handleStringRowChange(`nodes.${index}.endAtExistingNode`, value || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={hasError(`nodes.${index}.endAtExistingNode`)}
                      helperText={getFieldErrorText(index, 'endAtExistingNode')}
                    />
                  )}
                  disabled={index !== stringRows.length - 1}
                  fullWidth
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StringNodesTable;
